import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import CheckBox from '../components/checkBox/CheckBox';
import TextArea from '../components/textarea/TextArea';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import DownloadAnnexModal from '../components/modals/DownloadAnnexModal';
import { routes } from '../routes/routePaths';
import { 
  getAnnex, 
  createAnnex, 
  updateAnnex, 
  AnnexItem, 
  CreateAnnexRequest 
} from '../services/AnnexesService';

const AnnexDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== 'new');

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(!isEditing);
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  
  const [formData, setFormData] = useState<CreateAnnexRequest>({
    name: '',
    requires_year: false,
    requires_month: false,
    query: ''
  });

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    query?: string;
  }>({});

  useEffect(() => {
    const fetchAnnexDetail = async () => {
      if (!id || id === 'new') return;

      setLoading(true);
      setError(null);

      try {
        const response = await getAnnex(id);
        if (response.success && 'data' in response) {
          const annexData: AnnexItem = response.data;
          setFormData({
            name: annexData.name,
            requires_year: annexData.requires_year,
            requires_month: annexData.requires_month,
            query: annexData.query
          });
        } else {
          setError('Error al cargar el anexo');
          navigate(routes.annexes);
        }
      } catch (err) {
        setError('Error al cargar el anexo');
        navigate(routes.annexes);
      } finally {
        setLoading(false);
      }
    };

    if (isEditing) {
      fetchAnnexDetail();
    }
  }, [id, isEditing, navigate]);

  const handleGoBack = () => {
    navigate(routes.annexes);
  };

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    if (!formData.query.trim()) {
      errors.query = 'La consulta es requerida';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      let response;
      if (isEditing) {
        response = await updateAnnex(id!, formData);
        if (response.success) {
          setIsEditMode(false);
        }
      } else {
        response = await createAnnex(formData);
        if (response.success) {
          navigate(routes.annexes);
        }
      }

      if (!response.success) {
        setError('Error al guardar el anexo');
      }
    } catch (err) {
      setError('Error al guardar el anexo');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof CreateAnnexRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={4} columns={2} />
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a anexos"
        />
        <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
          {isEditing ? 'Editar anexo' : 'Crear nuevo anexo'}
        </H>
      </Card>

      {error && (
        <Card margin={{ bottom: '4' }} spacing="3" variant="danger" modifier="weak">
          {error}
        </Card>
      )}

      {/* Form */}
      <Card margin={{ top: '4' }}>
        {/* Name Input */}
        <Card margin={{ bottom: '3' }}>
            <Input
              label="Nombre"
              type="text"
              size="md"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              message={validationErrors.name}
              placeholder="Ingrese el nombre del anexo"
              disabled={!isEditMode}
            />
          </Card>

        {/* Requirements Checkboxes */}
        <Card margin={{ bottom: '2' }}>
          <Card display='flex' gap='24px'>
            <CheckBox
              id="requires_year"
              checked={formData.requires_year}
              onChange={(checked) => handleInputChange('requires_year', checked)}
              disabled={!isEditMode}
            >
              Requiere año
            </CheckBox>
            <CheckBox
              id="requires_month"
              checked={formData.requires_month}
              onChange={(checked) => handleInputChange('requires_month', checked)}
              disabled={!isEditMode}
            >
              Requiere mes
            </CheckBox>
          </Card>
        </Card>        {/* Query TextArea */}
        <Card margin={{ bottom: '4' }}>
          <TextArea
            label="Query"
            value={formData.query}
            onChange={(e) => handleInputChange('query', e.target.value)}
            status={validationErrors.query ? 'error' : 'normal'}
            message={validationErrors.query}
            placeHolder="Ingrese la consulta SQL"
            rows={20}
            resize={true}
            height="100%"
            disabled={!isEditMode}
          />
        </Card>

        {/* Save Button */}
        <Card display='flex' justifyContent={isEditing && !isEditMode ? 'space-between' : 'flex-end'}>
          {isEditing && !isEditMode && (
            <Button
              variant='secondary'
              onClick={handleEditMode}
              icon='edit_outline'
              iconAlign="left"
              iconSize={8}
            >
              Editar
            </Button>
          )}
          
          <Card display='flex' gap='16px'>
            {isEditing && !isEditMode && (
              <Button
                variant='primary'
                onClick={handleDownload}
                icon='download_outline'
                iconAlign="left"
              >
                Descargar
              </Button>
            )}
            
            {isEditMode && (
              <Button
                variant='primary'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            )}
          </Card>
        </Card>
      </Card>

      {isEditing && formData.name && (
        <DownloadAnnexModal
          isOpen={showDownloadModal}
          onClose={handleCloseDownloadModal}
          annexId={parseInt(id!)}
          annexName={formData.name}
          requiresYear={formData.requires_year}
          requiresMonth={formData.requires_month}
        />
      )}
    </Card>
  );
};

export default AnnexDetail;